import React from "react";
import { AxiosInstance } from "axios";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { useParams, useHistory } from "react-router-dom";

import { useClient } from "utils/client";
import { Project, TranslatedStandard } from "types";
import { useConfirmation } from "context/Confirmation";
import { BasePage } from "components/BasePage/BasePage";
import { StandardSearch } from "components/StandardSearch";
import { useAllTranslatedStandards } from "hooks/useStandards";
import { DeleteButton } from "components/Button/DeleteButton";
import { Feature, useFeatureFlags } from "context/FeatureFlag";
import { AIStandardResearch } from "components/AIStandardResearch";
import { AIStandardSuggestion } from "components/AIStandardResearch/types";
import { useProjectDetails } from "./Details/hooks/useProjectDetails";
import { useRisks } from "pages/Risk/hooks/useRisks";

interface StandardSearchRouteParams {
  projectId: string;
}

interface StandardSelection {
  isSelected: boolean;
  standardId: string;
  name: string;
}

export interface FetchStandardResponse {
  projectId: string;
  standards: StandardSelection[] | null;
}

const postStandards = (
  client: AxiosInstance,
  projectId: string,
  project: Project,
  selectedStandards: string[],
) => {
  return client.put(`/projects/${projectId}`, {
    payload: {
      ...project,
      projectdata: {
        ...project.projectdata,
        completed_research: true,
      },
      standards: selectedStandards,
    },
  });
};

const Standards = () => {
  const client = useClient();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { projectId } = useParams<StandardSearchRouteParams>();
  const { project, fetchInProgress } = useProjectDetails(projectId);
  // TODO: need to figure a way to pass multiple directives too
  // For now, this can only be EITHER mrl OR lvd
  const { translations: translatedStandards } = useAllTranslatedStandards();
  const { risks } = useRisks(projectId);
  const [selectedStandards, setSelectedStandards] = React.useState<string[]>(
    [],
  );
  const { t } = useTranslation();
  const { isFeatureEnabled } = useFeatureFlags();
  const showAIResearch = isFeatureEnabled(Feature.AIStandardResearch);

  type SearchMode = "lexical" | "ai";
  const [searchMode, setSearchMode] = React.useState<SearchMode>("lexical");
  const [aiSuggestions, setAISuggestions] = React.useState<
    AIStandardSuggestion[]
  >([]);
  const [aiSummary, setAISummary] = React.useState<string>("");
  const [aiDismissedIds, setAIDismissedIds] = React.useState<string[]>([]);
  const [aiIsLoading, setAIIsLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    const allStd = [...(project?.standards || [])];

    setSelectedStandards(allStd);
  }, [project?.standards]);

  const riskStandards = React.useMemo(() => {
    if (!risks) return [];

    const seen = new Set();

    const result: string[] = [];
    for (const risk of risks || []) {
      for (const mitigation of risk.mitigations || []) {
        for (const std of mitigation.applied_standards || []) {
          const id = std.split(":")[0];
          if (!seen.has(id)) {
            seen.add(id);
            result.push(std);
          }
        }
      }
    }
    return result;
  }, [risks]);

  const riskStandardSources = React.useMemo(() => {
    if (!risks)
      return {} as Record<string, { label: string; resourceId: string }[]>;
    const map: Record<string, { label: string; resourceId: string }[]> = {};
    for (const risk of risks || []) {
      for (const mitigation of risk.mitigations || []) {
        for (const std of mitigation.applied_standards || []) {
          const id = std.split(":")[0];
          if (!map[id]) map[id] = [];
          const label = risk.readable_name || risk.description;
          if (!map[id].find((e) => e.resourceId === risk.resource_id)) {
            map[id].push({ label, resourceId: risk.resource_id });
          }
        }
      }
    }
    return map;
  }, [risks]);

  const handleDelete = (name: string) => {
    const std = selectedStandards.find((std) => std.split(":")[0] === name);
    const idx = selectedStandards.indexOf(std!);

    const stds = [...selectedStandards];
    stds.splice(idx, 1);

    setSelectedStandards(stds);
  };

  const onSelect = (standard: TranslatedStandard) => {
    const selectedStandardWithVersion =
      standard.name && standard.latest
        ? [standard.name, standard.latest].join(":")
        : standard.name;

    setSelectedStandards(
      [selectedStandardWithVersion, ...selectedStandards].filter(
        (item, i, arr) => arr.findIndex((std) => std === item) === i,
      ),
    );
  };

  if (fetchInProgress) return null;

  return (
    <BasePage
      breadcrumbItems={[
        { label: "projects", location: "/dashboard" },
        {
          label: `${project?.projectdata.product_name || projectId}`,
          location: `/projects/${projectId}`,
        },
        {
          label: "standards.search.headline",
          location: `/projects/${projectId}/edit/standards`,
        },
      ]}
    >
      <div className="overflow-hidden">
        {showAIResearch && (
          <div className="mx-4 mt-4 mb-2">
            <div className="inline-flex rounded-lg bg-gray-100 p-1 gap-1.5">
              <button
                type="button"
                onClick={() => setSearchMode("lexical")}
                disabled={aiIsLoading}
                className={`relative rounded-md px-4 py-2.5 text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-1 ${
                  searchMode === "lexical"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                } ${aiIsLoading ? "pointer-events-none opacity-50" : ""}`}
              >
                <span className="flex items-center gap-2">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  {t("standards.aiResearch.tabLexical")}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setSearchMode("ai")}
                disabled={aiIsLoading}
                className={`relative rounded-md px-4 py-2.5 text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-1 ${
                  searchMode === "ai"
                    ? "bg-gradient-to-r from-cyan-600 to-cyan-500 text-white shadow-md border-2 border-transparent"
                    : "snake-border text-gray-700 hover:text-gray-900"
                } ${aiIsLoading ? "pointer-events-none opacity-50" : ""}`}
                style={
                  searchMode !== "ai"
                    ? ({ "--snake-bg": "#f3f4f6" } as React.CSSProperties)
                    : undefined
                }
              >
                <span className="flex items-center gap-2">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                  {t("standards.aiResearch.tab")}
                  <span
                    className={`ml-0.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide ${
                      searchMode === "ai"
                        ? "bg-white/20 text-white"
                        : "bg-cyan-100 text-cyan-700"
                    }`}
                  >
                    {t("standards.aiResearch.badge")}
                  </span>
                </span>
              </button>
            </div>
          </div>
        )}

        {(!showAIResearch || searchMode === "lexical") && (
          <StandardSearch
            onSelect={onSelect}
            // TODO: need to figure a way to pass multiple directives too
            // For now, this can only be EITHER mrl OR lvd
            // directive={project?.directives[0].name}
          />
        )}

        {showAIResearch && searchMode === "ai" && (
          <AIStandardResearch
            projectId={projectId}
            selectedStandards={selectedStandards}
            onSelect={onSelect}
            suggestions={aiSuggestions}
            setSuggestions={setAISuggestions}
            summary={aiSummary}
            setSummary={setAISummary}
            dismissedIds={aiDismissedIds}
            setDismissedIds={setAIDismissedIds}
            setIsLoading={setAIIsLoading}
          />
        )}
      </div>

      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="flex-1 min-w-0"></div>
        <div className="mt-4 flex sm:mt-0 sm:ml-4">
          <button
            onClick={async () => {
              await postStandards(
                client,
                projectId,
                project!,
                selectedStandards,
              );
              await queryClient.invalidateQueries(["projects", projectId]);

              history.push(`/projects/${projectId}?saved=true`);
            }}
            type="button"
            className="order-0 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 sm:order-1 sm:ml-3"
          >
            {t("standards.search.table.save_standards")}
          </button>
        </div>
      </div>
      <div className="hidden mt-8 sm:block">
        <h2 className="text-base font-medium text-gray-700 mb-3">
          {t("standards.search.table.title")}
        </h2>
        <div className="align-middle inline-block min-w-full">
          <table className="shadow-md">
            <thead>
              <tr>
                <th className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <span className="">
                    {t("standards.search.table.standard")}
                  </span>
                </th>
                <th className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <span className="">
                    {t("standards.search.table.directive")}
                  </span>
                </th>

                <th className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("standards.search.table.type")}
                </th>

                <th className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("standards.search.table.latest_version")}
                </th>

                <th className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("standards.search.table.standard_text")}
                </th>

                <th className="pr-6 py-3 border-b border-gray-200 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {uniqueStd(selectedStandards).map((standard) => {
                const translation = translatedStandards?.find(
                  (t) => t.name.split(":")[0] === standard.split(":")[0],
                );

                return (
                  <StandardRow
                    key={standard}
                    standard={standard}
                    onDelete={handleDelete}
                    additionalData={translation}
                    translation={translation?.content}
                    isNew={
                      !project?.standards?.find(
                        (std) => std.split(":")[0] === standard,
                      )
                    }
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {riskStandards.length > 0 && (
        <div className="hidden mt-10 sm:block">
          <h2 className="text-base font-medium text-gray-700 mb-3">
            {t("standards.search.table.from_risks_title")}
          </h2>
          <div className="align-middle inline-block min-w-full">
            <table className="shadow-md">
              <thead>
                <tr>
                  <th className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span>{t("standards.search.table.standard")}</span>
                  </th>
                  <th className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span>{t("standards.search.table.directive")}</span>
                  </th>
                  <th className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("standards.search.table.type")}
                  </th>
                  <th className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("standards.search.table.latest_version")}
                  </th>
                  <th className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("standards.search.table.standard_text")}
                  </th>
                  <th className="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("standards.search.table.risks")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {uniqueStd(riskStandards).map((standard) => {
                  const translation = translatedStandards?.find(
                    (t) => t.name.split(":")[0] === standard.split(":")[0],
                  );
                  return (
                    <ReadOnlyStandardRow
                      key={standard}
                      standard={standard}
                      additionalData={translation}
                      translation={translation?.content}
                      projectId={projectId}
                      sourceRisks={riskStandardSources[standard] || []}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </BasePage>
  );
};

const uniqueStd = (stds: string[]) => {
  return stds
    .map((std) => std.split(":")[0])
    .filter((value, index, self) => {
      return self.indexOf(value) === index;
    });
};

export default Standards;

const ReadOnlyStandardRow = (props: {
  standard: string;
  translation?: string;
  additionalData?: TranslatedStandard;
  projectId: string;
  sourceRisks: { label: string; resourceId: string }[];
}) => {
  const { t } = useTranslation();

  return (
    <tr className="bg-gray-50">
      <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
        <div className="flex items-center space-x-3 lg:pl-2">
          <div
            className="flex-shrink-0 w-2.5 h-2.5 rounded-full"
            aria-hidden="true"
          ></div>
          <span>{props.standard.replace(/:$/, "")}</span>{" "}
          {props.additionalData?.retracted && (
            <span className="text-ms text-red-400">Zurückgezogen</span>
          )}
        </div>
      </td>

      <td className="px-6 py-3 text-sm text-gray-500 font-medium">
        {t(`directives.enum.${props.additionalData?.directive}`, {
          defaultValue: props.additionalData?.directive,
        }) || "..."}
      </td>

      <td className="px-6 py-3 text-sm text-gray-500 font-medium">
        {props.additionalData?.type || "..."}
      </td>

      <td className="px-6 py-3 text-sm text-gray-500 font-medium">
        {props.additionalData?.latest || "..."}
      </td>

      <td className="px-6 py-3 text-sm text-gray-500 font-medium">
        {props.translation || "..."}
      </td>

      <td className="px-6 py-3 text-sm text-gray-500 font-medium">
        <div className="flex flex-col gap-1">
          {props.sourceRisks.map(({ label, resourceId }) => (
            <a
              key={resourceId}
              target="_blank"
              rel="noreferrer"
              href={`/projects/${props.projectId}/risks/${resourceId}/edit`}
              className="text-cyan-600 hover:underline"
            >
              {label}{" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="inline h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
              </svg>
            </a>
          ))}
        </div>
      </td>
    </tr>
  );
};

const StandardRow = (props: {
  isNew: boolean;
  standard: string;
  translation?: string;
  additionalData?: TranslatedStandard;
  onDelete: (id: string) => void;
}) => {
  const { openConfirmation } = useConfirmation();
  const { t } = useTranslation();

  const handleDelete = () => {
    openConfirmation(
      () => {
        props.onDelete(props.standard);
      },
      {
        label: t("form.basic.label"),
        subline: t("standards.search.table.delete_confirmation_subline"),
      },
    );
  };

  return (
    <tr>
      <td className="px-6 py-3  whitespace-nowrap text-sm font-medium text-gray-900">
        <div className="flex items-center space-x-3 lg:pl-2">
          <div
            className={`flex-shrink-0 w-2.5 h-2.5 rounded-full ${
              props.isNew ? "bg-green-500" : ""
            }`}
            aria-hidden="true"
          ></div>
          <span>{props.standard.replace(/:$/, "")}</span>{" "}
          {props.additionalData?.retracted && (
            <span className="text-ms text-red-400">Zurückgezogen</span>
          )}
        </div>
      </td>

      <td className="px-6 py-3 text-sm text-gray-500 font-medium">
        {t(`directives.enum.${props.additionalData?.directive}`, {
          defaultValue: props.additionalData?.directive,
        }) || "..."}
      </td>

      <td className="px-6 py-3 text-sm text-gray-500 font-medium">
        {props.additionalData?.type || "..."}
      </td>

      <td className="px-6 py-3 text-sm text-gray-500 font-medium">
        {props.additionalData?.latest || "..."}
      </td>

      <td className="px-6 py-3 text-sm text-gray-500 font-medium">
        {props.translation || "..."}
      </td>

      <td className="pr-6">
        <div className="relative flex justify-end items-center">
          <DeleteButton flat={true} onClick={handleDelete} />
        </div>
      </td>
    </tr>
  );
};
